import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import useStore from '../../store/useStore';
import { metricsAPI, savedReportsAPI, reportsAPI, statementsAPI, kpisAPI } from '../../services/api';
import useModalOverlayClose from '../../hooks/useModalOverlayClose';
import useDialogKeys from '../../hooks/useDialogKeys';
import PackLogo from '../data-sources/PackLogo';
import WidgetPreview from './WidgetPreview';
import { packColor } from '../data-sources/packBrands';
import '../../styles/DashboardEditor.css';
import '../../styles/DataSources.css';

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'reports', label: 'Reports' },
  { id: 'finance', label: 'Statements' },
  { id: 'kpi', label: 'KPIs' },
  { id: 'live', label: 'Live metrics' },
];

const DISPLAY_LABEL = { table: 'Table', pivot: 'Pivot table', stat: 'Single number', bar: 'Bar chart', line: 'Line chart', area: 'Area chart', pie: 'Pie chart' };
const METRIC_THUMB = { gauge: 'gauge', count: 'stat', table: 'table', service_status: 'status', line_chart: 'line', area_chart: 'area', chart: 'line' };
const CADENCE_LABEL = { day: 'Daily', week: 'Weekly', month: 'Monthly', quarter: 'Quarterly', year: 'Yearly' };
const GENERAL = 'general';

/** A small drawing of what the widget looks like. Decorative: the card's text says what it is. */
function WidgetThumb({ type }) {
  const shapes = {
    bar: <><rect x="6" y="22" width="7" height="12" rx="1" /><rect x="17" y="12" width="7" height="22" rx="1" /><rect x="28" y="17" width="7" height="17" rx="1" /><rect x="39" y="7" width="7" height="27" rx="1" /></>,
    line: <polyline points="5,30 16,21 26,25 36,12 47,8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />,
    area: <path d="M5 34V27l11-8 10 4 10-12 11-5v28z" />,
    pie: <><path d="M26 20V5a15 15 0 0 1 13 22.5z" /><path d="M26 20l13 7.5A15 15 0 1 1 26 5z" opacity="0.45" /></>,
    table: <><rect x="6" y="7" width="40" height="6" rx="1" /><rect x="6" y="17" width="40" height="3" rx="1" opacity="0.45" /><rect x="6" y="24" width="40" height="3" rx="1" opacity="0.45" /><rect x="6" y="31" width="40" height="3" rx="1" opacity="0.45" /></>,
    pivot: <><rect x="6" y="7" width="40" height="6" rx="1" /><rect x="6" y="16" width="10" height="18" rx="1" /><rect x="19" y="16" width="27" height="18" rx="1" opacity="0.45" /></>,
    stat: <><rect x="10" y="10" width="32" height="13" rx="2" /><rect x="16" y="28" width="20" height="3" rx="1" opacity="0.45" /></>,
    statement: <><rect x="6" y="6" width="22" height="3" rx="1" /><rect x="36" y="6" width="10" height="3" rx="1" /><rect x="6" y="14" width="18" height="3" rx="1" opacity="0.45" /><rect x="36" y="14" width="10" height="3" rx="1" opacity="0.45" /><rect x="6" y="22" width="18" height="3" rx="1" opacity="0.45" /><rect x="36" y="22" width="10" height="3" rx="1" opacity="0.45" /><rect x="6" y="31" width="40" height="3" rx="1" /></>,
    kpi: <><rect x="5" y="9" width="12" height="22" rx="2" /><rect x="20" y="9" width="12" height="22" rx="2" opacity="0.6" /><rect x="35" y="9" width="12" height="22" rx="2" opacity="0.35" /></>,
    gauge: <><path d="M9 30a17 17 0 0 1 34 0" fill="none" stroke="currentColor" strokeWidth="5" opacity="0.35" /><path d="M9 30a17 17 0 0 1 20-16.7" fill="none" stroke="currentColor" strokeWidth="5" /></>,
    status: <><circle cx="11" cy="12" r="4" /><rect x="19" y="10" width="27" height="4" rx="1" opacity="0.45" /><circle cx="11" cy="27" r="4" /><rect x="19" y="25" width="27" height="4" rx="1" opacity="0.45" /></>,
  };
  return (
    <span className="library-thumb" aria-hidden="true">
      <svg viewBox="0 0 52 40" fill="currentColor">{shapes[type] || shapes.bar}</svg>
    </span>
  );
}

/**
 * The widget library: everything that can go on a dashboard, in a searchable
 * modal grouped by where it comes from. Adding keeps it open so several widgets
 * can be added in a row; each lands in the first free gap on the grid.
 */
const WidgetLibrary = ({ widgets = [], onAdd, onClose, busyKey = null, lastAddedKey = null }) => {
  const companyId = useStore((state) => state.currentCompany?.id);
  const overlay = useModalOverlayClose(onClose);
  const dialogRef = useRef(null);
  const searchRef = useRef(null);
  const startCount = useRef(widgets.length);
  const [category, setCategory] = useState('all');
  const [source, setSource] = useState('all');
  const [search, setSearch] = useState('');
  const [previewKey, setPreviewKey] = useState(null);
  const [data, setData] = useState({ loading: true, metrics: [], reports: [], statements: [], library: [], kpis: [], sources: {} });

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    const ok = (p) => p.then((r) => (r?.success ? r.data : null)).catch(() => null);
    Promise.all([
      ok(metricsAPI.getDefinitions(companyId)),
      ok(savedReportsAPI.list(companyId)),
      ok(statementsAPI.list(companyId)),
      ok(reportsAPI.library(companyId)),
      ok(kpisAPI.list(companyId)),
    ]).then(([metrics, reports, statements, library, kpis]) => {
      if (cancelled) return;
      setData({ loading: false, metrics: metrics || [], reports: reports || [], statements: statements || [], library: library?.reports || [], kpis: (kpis || []).filter((k) => k.isActive !== false), sources: library?.datasetSources || {} });
    });
    return () => { cancelled = true; };
  }, [companyId]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // Escape clears a search first, then closes. While a preview is open, the keyboard belongs to it.
  useDialogKeys(dialogRef, () => {
    if (searchRef.current?.value) setSearch('');
    else onClose?.();
  }, previewKey === null);

  const onDashboard = useMemo(() => ({
    metrics: new Set(widgets.filter((w) => !w.widget_kind || w.widget_kind === 'metric').map((w) => w.metric_key)),
    reports: new Set(widgets.filter((w) => w.report).map((w) => w.report.id)),
    statements: new Set(widgets.filter((w) => w.widget_kind === 'statement').map((w) => `${w.dataset_name}:${w.config?.layoutKey}`)),
    kpiTiles: new Set(widgets.filter((w) => w.widget_kind === 'kpi').map((w) => w.config?.kpiIds?.[0])),
  }), [widgets]);

  const items = useMemo(() => {
    const packOf = (datasetName) => data.sources[datasetName]?.pack || null;
    const datasetTitle = (datasetName) => data.sources[datasetName]?.title || datasetName;
    const out = [];
    for (const m of data.metrics) {
      out.push({
        key: `metric:${m.metric_key}`, category: 'live', thumb: METRIC_THUMB[m.widget_type] || 'stat',
        title: m.display_name || m.metric_key, detail: `${(m.widget_type || 'value').replace(/_/g, ' ')} · updates live`, pack: null,
        added: onDashboard.metrics.has(m.metric_key), action: { kind: 'metric', metric: m },
      });
    }
    // A ready-made report that has been saved already is offered once, as the saved report.
    const savedNames = new Set(data.reports.map((r) => `${r.datasetName}:${r.name}`));
    const readyMade = new Set(data.library.map((l) => `${l.datasetName}:${l.title}`));
    for (const r of data.reports) {
      const type = r.display?.displayType || 'table';
      out.push({
        key: `report:${r.id}`, category: 'reports', thumb: type,
        title: r.name, detail: r.description || `${DISPLAY_LABEL[type] || type} · ${datasetTitle(r.datasetName)}`,
        pack: packOf(r.datasetName), tag: r.visibility === 'private' ? 'Only you' : readyMade.has(`${r.datasetName}:${r.name}`) ? 'Ready-made' : 'Saved report',
        added: onDashboard.reports.has(r.id), action: { kind: 'report', report: r },
      });
    }
    for (const s of data.statements) {
      out.push({
        key: `statement:${s.datasetName}:${s.key}`, category: 'finance', thumb: 'statement',
        title: s.title, detail: s.missing.length ? `Needs ${s.missing.join(', ')}` : s.description || datasetTitle(s.datasetName),
        pack: packOf(s.datasetName), tag: 'Statement',
        disabled: s.missing.length > 0, added: onDashboard.statements.has(`${s.datasetName}:${s.key}`), action: { kind: 'statement', layout: s },
      });
    }
    for (const k of data.kpis) {
      out.push({
        key: `kpi:${k.id}`, category: 'kpi', thumb: 'stat',
        title: k.name, detail: [k.department, CADENCE_LABEL[k.cadence]].filter(Boolean).join(' · '),
        // A KPI's measure is written cube.member, and a cube is named after its dataset.
        pack: packOf(String(k.measure || '').split('.')[0]), tag: 'KPI',
        added: onDashboard.kpiTiles.has(k.id), action: { kind: 'kpi', kpi: { id: k.id, name: k.name } },
      });
    }
    for (const l of data.library) {
      if (savedNames.has(`${l.datasetName}:${l.title}`)) continue;
      out.push({
        key: `library:${l.datasetName}:${l.key}`, category: 'reports', thumb: l.displayType,
        title: l.title, detail: `${DISPLAY_LABEL[l.displayType] || l.displayType} · ${l.datasetTitle || l.datasetName}`,
        pack: l.packId ? { id: l.packId, title: l.packTitle } : packOf(l.datasetName), tag: 'Ready-made',
        action: { kind: 'library', item: l },
      });
    }
    return out;
  }, [data, onDashboard]);

  const sourceOf = (item) => item.pack?.id || GENERAL;

  const term = search.trim().toLowerCase();
  const matchesSearch = (i) => !term || term.split(/\s+/).every((word) => `${i.title} ${i.detail} ${i.tag || ''} ${i.pack?.title || ''}`.toLowerCase().includes(word));
  const searched = items.filter(matchesSearch);

  const sources = useMemo(() => {
    const packs = new Map();
    for (const i of items) if (i.pack) packs.set(i.pack.id, i.pack);
    const list = [...packs.values()].sort((a, b) => a.title.localeCompare(b.title));
    return items.some((i) => !i.pack) ? [...list, { id: GENERAL, title: 'General' }] : list;
  }, [items]);

  const inCategory = searched.filter((i) => category === 'all' || i.category === category);
  const shown = inCategory.filter((i) => source === 'all' || sourceOf(i) === source);
  const groups = sources
    .map((s) => ({ ...s, items: shown.filter((i) => sourceOf(i) === s.id).sort((a, b) => a.title.localeCompare(b.title)) }))
    .filter((g) => g.items.length > 0);

  const tabCount = (id) => searched.filter((i) => (id === 'all' || i.category === id) && (source === 'all' || sourceOf(i) === source)).length;
  const sourceCount = (id) => (id === 'all' ? inCategory.length : inCategory.filter((i) => sourceOf(i) === id).length);
  const tabs = CATEGORIES.filter((c) => c.id === 'all' || items.some((i) => i.category === c.id));
  const addedCount = Math.max(0, widgets.length - startCount.current);
  const clearFilters = () => { setSearch(''); setCategory('all'); setSource('all'); searchRef.current?.focus(); };

  const previewing = previewKey ? items.find((i) => i.key === previewKey) : null;

  // Rendered on the page body so it sits above the whole app, not inside the dashboard's layout.
  const library = createPortal(
    <div className="modal-overlay" {...overlay}>
      <div className="widget-library" role="dialog" aria-modal="true" aria-labelledby="widget-library-title" ref={dialogRef} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <header className="widget-library-header">
          <div className="widget-library-heading">
            <h2 id="widget-library-title">Add a widget</h2>
            <p>Widgets go into the first free space. You can drag them into position afterwards.</p>
          </div>
          <button type="button" className="editor-drawer-close" onClick={onClose} aria-label="Close">×</button>
          <div className="library-search-box">
            <svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M14 14l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            <input ref={searchRef} type="search" placeholder="Search by name, type or pack" aria-label="Search the widget library" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="library-categories" role="tablist" aria-label="Widget categories">
            {tabs.map((c) => (
              <button key={c.id} type="button" role="tab" aria-selected={category === c.id} className={`library-category ${category === c.id ? 'active' : ''}`} onClick={() => setCategory(c.id)}>
                {c.label} <span className="library-category-count">{tabCount(c.id)}</span>
              </button>
            ))}
          </div>
        </header>

        <div className="widget-library-main">
          {sources.length > 1 && (
            <nav className="library-sources" aria-label="Filter by source">
              <button type="button" aria-pressed={source === 'all'} className={`library-source ${source === 'all' ? 'active' : ''}`} onClick={() => setSource('all')}>
                <span className="library-source-name">All sources</span>
                <span className="library-source-count">{sourceCount('all')}</span>
              </button>
              {sources.map((s) => (
                <button key={s.id} type="button" aria-pressed={source === s.id} className={`library-source ${source === s.id ? 'active' : ''}`} onClick={() => setSource(s.id)}>
                  {s.id !== GENERAL && <PackLogo id={s.id} title={s.title} size="tiny" />}
                  <span className="library-source-name">{s.title}</span>
                  <span className="library-source-count">{sourceCount(s.id)}</span>
                </button>
              ))}
            </nav>
          )}

          <div className="library-results">
            {data.loading ? <div className="library-empty">Loading…</div> : groups.length === 0 ? (
              <div className="library-empty">
                <p>{term || category !== 'all' || source !== 'all' ? 'No widgets match your search and filters.' : 'There is nothing to add yet. Install a pack or create a report first.'}</p>
                {(term || category !== 'all' || source !== 'all') && <button type="button" className="btn-secondary" onClick={clearFilters}>Clear search and filters</button>}
              </div>
            ) : groups.map((group) => (
              <section key={group.id} className="library-group" aria-label={group.title}>
                <h3 className="library-group-title">
                  {group.id !== GENERAL && <PackLogo id={group.id} title={group.title} size="small" />}
                  <span>{group.title}</span>
                  {group.id !== GENERAL && <span className="library-pack-label">Pack</span>}
                  <span className="library-group-count">{group.items.length}</span>
                </h3>
                <ul className="library-grid" aria-label={`${group.title} widgets`}>
                  {group.items.map((item) => (
                    <li key={item.key} className={`library-card ${item.key === lastAddedKey ? 'library-card--added' : ''} ${item.disabled ? 'library-card--disabled' : ''}`} style={{ '--pack-color': item.pack ? packColor(item.pack.id) : undefined }}>
                      <div className="library-card-top">
                        <WidgetThumb type={item.thumb} />
                        <div className="library-card-text">
                          <strong>{item.title}</strong>
                          <small title={item.detail}>{item.detail}</small>
                        </div>
                      </div>
                      <div className="library-card-foot">
                        <span className="library-card-tags">
                          {item.pack && <span className="library-pack-chip"><PackLogo id={item.pack.id} title={item.pack.title} size="tiny" />{item.pack.title}</span>}
                          {item.tag && <span className="library-tag">{item.tag}</span>}
                          {item.added && <span className="library-tag library-tag--on">On dashboard</span>}
                        </span>
                        <span className="library-card-actions">
                          <button type="button" className="btn-secondary-small" disabled={item.disabled} aria-label={`Preview ${item.title}`} onClick={() => setPreviewKey(item.key)}>Preview</button>
                          <button type="button" className={item.added ? 'btn-secondary-small' : 'btn-primary-small'} disabled={item.disabled || busyKey !== null} aria-label={`Add ${item.title}`} onClick={() => onAdd(item.action, item.key)}>
                            {busyKey === item.key ? 'Adding…' : item.added ? 'Add again' : 'Add'}
                          </button>
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>

        <footer className="widget-library-footer">
          <span className="library-footer-note">New reports are created on the <Link to="/reports?new=1">Reports page</Link>.</span>
          <span className="library-footer-actions">
            {addedCount > 0 && <span className="library-added-count">{addedCount} added</span>}
            <button type="button" className="btn-primary" onClick={onClose}>Done</button>
          </span>
        </footer>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      {library}
      {previewing && <WidgetPreview item={previewing} busy={busyKey !== null} onAdd={onAdd} onClose={() => setPreviewKey(null)} />}
    </>
  );
};

export default WidgetLibrary;
